import React, { useRef, useState } from "react";
import CashIcon from "assets/images/icons/cash.svg";
import { useTranslation } from "react-i18next";
import { ErrorMessage, Form, Formik } from "formik";
import { Col, Row } from "react-bootstrap";
import CustomLabel from "components/common/CustomLabel";
import CustomField from "components/common/CustomField";
import { CircleX, Minus, Plus } from "lucide-react";
import CalenderIcon from "assets/images/icons/calendarPrimary.svg";
import { Calendar } from "primereact/calendar";
import {
  convertToUTCDateTimeString,
  formatDateTime,
} from "components/common/Utils";
import { addCashbackToCustomersSchema } from "components/common/Validation";
import { addRechargeToCustomerWallet } from "store/actions/customerSlice";
import { showAlert } from "store/reducers/alertReducer";
import { useDispatch } from "react-redux";
import { SuccessIcon } from "components/common/SVGIcons";

const initialValues = {
  dateAndTime: null,
  amount: null,
  title: "",
  titleAr: "",
  description: "",
  descriptionAr: "",
};

const AddCashbackForCustomers = ({
  selectedIds,
  setSelectedIds,
  setShow,
  getAllData,
}) => {
  const { t } = useTranslation();
  const calendarRef = useRef(null);
  const [step, setStep] = useState(1);
  const [formValues, setFormValues] = useState(initialValues);
  const dispatch = useDispatch();

  const handleOnSubmit = (values) => {
    setFormValues(values);
    setStep(2);
  };

  const handleSendCashback = () => {
    const { dateAndTime, amount, title, titleAr, description, descriptionAr } =
      formValues;
    const payload = {
      userIds: selectedIds,
      amount,
      expiryDate: convertToUTCDateTimeString(dateAndTime),
      title,
      title_ar: titleAr,
      description,
      description_ar: descriptionAr,
    };

    dispatch(addRechargeToCustomerWallet(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          setStep(3);
          setSelectedIds([]);
          setTimeout(() => {
            setShow(false);
          }, 3000);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  return (
    <div className="inner_section">
      {step === 1 ? (
        <>
          <div>
            <h2 className="fs_14 mb-2">{t("Recharge")}</h2>
            <Formik
              initialValues={formValues}
              validationSchema={addCashbackToCustomersSchema}
              onSubmit={handleOnSubmit}
              enableReinitialize
            >
              {({ values, setFieldValue, errors, touched }) => {
                const handleIncrement = (e) => {
                  e.preventDefault();
                  const newValue = Number(values.amount) + 1;
                  setFieldValue("amount", newValue);
                };

                const handleDecrement = (e) => {
                  e.preventDefault();
                  const newValue =
                    Number(values.amount) > 1 ? Number(values.amount) - 1 : 1;
                  setFieldValue("amount", newValue);
                };

                const handleInputChange = (e) => {
                  const inputValue = e.target.value;
                  if (/^\d*$/.test(inputValue)) {
                    setFieldValue(
                      "amount",
                      inputValue === "" ? "" : Number(inputValue)
                    );
                  }
                };
                return (
                  <Form>
                    <Row className="wallet_recharge ">
                      <Col lg={12}>
                        <p className="fs_12 text_gray">{t("Expiry Date")}</p>
                        <div
                          className={`date_wrapper g_between c_p ${
                            errors.dateAndTime && touched.dateAndTime
                              ? "border_danger"
                              : ""
                          }`}
                          onClick={() => calendarRef.current?.show()}
                        >
                          <Calendar
                            id="wallet_date_picker"
                            value={values.dateAndTime}
                            onChange={(e) =>
                              setFieldValue("dateAndTime", e.value)
                            }
                            showTime
                            hourFormat="12"
                            ref={calendarRef}
                            inputStyle={{
                              position: "absolute",
                              width: 0,
                              height: 0,
                              padding: 0,
                              margin: 0,
                              border: "none",
                              opacity: 0,
                              pointerEvents: "none",
                            }}
                            minDate={new Date()}
                            panelStyle={{ zIndex: 9999 }}
                          />
                          <div className="d-flex w-100 gap-2">
                            <img
                              src={CalenderIcon}
                              alt="icon"
                              width={18}
                              height={18}
                              className="mb-1"
                            />
                            <span className="text_black">
                              {formatDateTime(values.dateAndTime)}
                              {/* Feb 10, 12:00pm */}
                            </span>
                          </div>
                          {values.dateAndTime && (
                            <button
                              type="button"
                              className="transparent_btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setFieldValue("dateAndTime", null);
                              }}
                            >
                              <CircleX className="text_danger" size={18} />
                            </button>
                          )}
                        </div>
                        <ErrorMessage
                          name={`dateAndTime`}
                          component="div"
                          className="text-danger"
                        />
                      </Col>
                      <Col lg={12}>
                        <div className=" my-4">
                          <div className="buttons my-0">
                            <button
                              className="bg_primary text-white"
                              onClick={handleIncrement}
                            >
                              <Plus />
                            </button>
                            <input
                              type="number"
                              className="count-input"
                              value={values.amount}
                              onChange={(e) =>
                                handleInputChange(e, setFieldValue)
                              }
                              min="1"
                            />
                            <button
                              className="bg_danger text-white"
                              onClick={handleDecrement}
                            >
                              <Minus />
                            </button>
                          </div>
                          <ErrorMessage
                            name={`amount`}
                            component="div"
                            className="text-danger"
                          />
                        </div>
                      </Col>
                      <Col lg={12}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="title"
                            label={t("English Message")}
                          />
                          <CustomField
                            name="title"
                            type="text"
                            placeholder={t("Title")}
                            touched={touched}
                            errors={errors}
                          />
                        </div>
                      </Col>
                      <Col lg={12}>
                        <div className="form_field">
                          <CustomField
                            name="description"
                            placeholder="Message"
                            touched={touched}
                            errors={errors}
                            as="textarea"
                          />
                        </div>
                      </Col>

                      <Col lg={12}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="titleAr"
                            label={t("Arabic Message")}
                          />
                          <CustomField
                            name="titleAr"
                            type="text"
                            placeholder={t("Title")}
                            touched={touched}
                            errors={errors}
                            isArabic={true}
                          />
                        </div>
                      </Col>
                      <Col lg={12}>
                        <div className="form_field">
                          <CustomField
                            name="descriptionAr"
                            placeholder={t("Message")}
                            touched={touched}
                            errors={errors}
                            as="textarea"
                            isArabic={true}
                          />
                        </div>
                      </Col>
                      <Col lg={12}>
                        <button className="main_btn w-100">
                          {t("Recharge")}
                        </button>
                      </Col>
                    </Row>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </>
      ) : step === 2 ? (
        <div>
          <img
            src={CashIcon}
            className="d-block mx-auto"
            alt=""
            width={70}
            height={70}
          />
          <h2 className="fs_20 text-center mt-4">
            {t("Cashback will be sent to {{value}} clients", {
              value: selectedIds?.length,
            })}
          </h2>
          <div className="buttons d-flex gap-3 mt-5">
            <button className="cancel_btn w-50" onClick={() => setStep(1)}>
              {t("Cancel")}
            </button>
            <button className="main_btn w-50" onClick={handleSendCashback}>
              {t("Send")}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <SuccessIcon/>
          <h2
            className="fs_20 text-center mt-4 text_black d-block mx-auto"
            style={{ maxWidth: 242 }}
          >
            {t("Cashback has been successfully sent!")}
          </h2>
        </div>
      )}
    </div>
  );
};

export default AddCashbackForCustomers;
